export const DLOCAL_NOTIFICATION_TENANT_ID = '80ca91bd-bc50-40c0-90b7-f0e65294d10e';

export const NOTIFICATION_CHANNEL_FEATURES = {
  inApp: 'Notification Preferences',
  email: 'Email Notification Preferences',
  slack: 'Slack Notification Preferences',
  weekly: 'Weekly Notification Preferences',
};

// Firestore contains both JSON strings and maps. Never use truthiness for a
// stored value: the legacy string "false" must remain an explicit opt-out.
export function normalizeNotificationPreferences(value) {
  const parsed = typeof value === 'string' ? JSON.parse(value) : value;
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new Error('Notification preferences are unavailable');
  }
  return Object.fromEntries(Object.entries(parsed).map(([key, enabled]) => {
    if (enabled === true || enabled === 'true') return [key, true];
    if (enabled === false || enabled === 'false') return [key, false];
    throw new Error(`Invalid notification preference: ${key}`);
  }));
}

function readPreference(response, tenantId) {
  if (!response) return { status: 'missing', preferences: {} };
  const record = response.data ?? response;
  if (!record || record.isMissing || (tenantId === DLOCAL_NOTIFICATION_TENANT_ID && record.isDefault)) {
    return { status: 'missing', preferences: {} };
  }
  return { status: 'ready', preferences: normalizeNotificationPreferences(record.preferences) };
}

function fetchChannel(service, userId, tenantId, channel) {
  const feature = NOTIFICATION_CHANNEL_FEATURES[channel];
  if (channel === 'slack') return service.getPreferencesByTenantIdAndFeature(tenantId, feature);
  if (!userId) throw new Error('User ID not found');
  return service.getUserPreferencesByUserIdAndFeature(userId, feature, tenantId);
}

export async function loadNotificationSettings(service, userId, tenantId) {
  const entries = await Promise.all(Object.keys(NOTIFICATION_CHANNEL_FEATURES).map(async channel => {
    try {
      return [channel, readPreference(await fetchChannel(service, userId, tenantId, channel), tenantId)];
    } catch (error) {
      // A missing record is different from a failed request or corrupt record.
      // The user must choose to configure it before any preference can be saved.
      return [channel, error.response?.status === 404
        ? { status: 'missing', preferences: {} }
        : { status: 'error', preferences: {} }];
    }
  }));
  return {
    preferences: Object.fromEntries(entries.map(([channel, result]) => [channel, result.preferences])),
    status: Object.fromEntries(entries.map(([channel, result]) => [channel, result.status])),
  };
}

export async function saveNotificationSetting(service, { userId, tenantId, channel, category, enabled }) {
  const feature = NOTIFICATION_CHANNEL_FEATURES[channel];
  if (!feature || !tenantId || typeof enabled !== 'boolean') throw new Error('Invalid notification preference');
  if (channel !== 'slack' && !userId) throw new Error('User ID not found');

  // Preferences are patched by category on the server, so another saved
  // choice is never replaced with a stale value from this browser tab.
  const data = { preferences: JSON.stringify({ [category]: enabled }), tenantId };
  try {
    if (channel === 'slack') {
      await service.updateDashboardPreferencesByTenantIdAndFeature(tenantId, feature, data);
    } else {
      await service.updateUserPreferencesByUserIdAndFeature(userId, feature, data);
    }
  } catch (error) {
    if (error.response?.status !== 404) throw error;
    if (channel === 'slack') {
      await service.createDashboardPreferences({ ...data, feature });
    } else {
      await service.createUserPreferences({ ...data, userId, feature });
    }
  }

  const saved = readPreference(await fetchChannel(service, userId, tenantId, channel), tenantId);
  if (saved.status !== 'ready' || saved.preferences[category] !== enabled) {
    throw new Error('The saved notification preference could not be verified');
  }
  return saved.preferences;
}
